import { Component, Input, Output, EventEmitter } from "@angular/core";
import { CommonModule } from "@angular/common";

@Component({
  selector: "app-tab-button",
  templateUrl: "./tab-button.component.html",
})
export class TabButtonComponent {
  // tw-button-active paints its own gradient background-image (see tailwind.config.js), which
  // visually covers any background-color — including this one — so it's only safe to combine
  // with the default fill below. Any other activeBg skips tw-button-active (see template).
  protected readonly defaultActiveBg = "var(--color-surface-card)";

  @Input() label: string = "";
  @Input() isActive: boolean = false;
  @Input() activeBg: string = this.defaultActiveBg;
  // Opt-in: the default look (rounded-2xl, py-3, shadow-card-button) stays as-is for every
  // existing usage. Pages that need the tighter "segmented control" shape/shadow pass this
  // instead of restyling the shared default (see feedback_shared_component_scope memory).
  @Input() segmented: boolean = false;
  @Output() tabClick = new EventEmitter<void>();

  protected get buttonClass(): string {
    // Segmented: inset the :focus-visible outline — the track's 4px padding and gap leave no room
    // for it outside, so it ran into the track edge and the neighbouring segment.
    const shape = this.segmented
      ? "tw-py-[9px] tw-px-4 tw-rounded-xl focus-visible:-tw-outline-offset-2"
      : "tw-py-3 tw-rounded-2xl";
    const size = this.segmented ? "tw-text-[13px]" : "tw-text-sm";

    if (!this.isActive) {
      // Label/Default (weight 500, color/text/tertiary) for the segmented variant; unchanged
      // tw-font-medium + text-default for every other existing usage.
      const textColor = this.segmented
        ? "tw-text-color-text-tertiary"
        : "tw-text-color-text-default";
      return `${shape} ${size} tw-font-medium ${textColor}`;
    }

    // The segmented shadow's white highlight glows on dark surfaces: dark-theme.scss swaps in the
    // dark-safe control elevation token (.theme_dark .tw-shadow-segmented).
    const shadow = this.segmented ? "tw-shadow-segmented" : "tw-shadow-card-button";
    // Label/Strong (weight 600, color/text/default) for the segmented variant when active.
    const weight = this.segmented ? "tw-font-semibold" : "tw-font-medium";
    const activeClass = this.activeBg === this.defaultActiveBg ? " tw-button-active" : "";
    return `${shape} ${shadow} ${size} ${weight} tw-text-color-text-default${activeClass}`;
  }

  onClick(): void {
    this.tabClick.emit();
  }
}
